/**
 * Expand Group Therapy note Questionnaire prototypes into one per-patient
 * section (ratings, comments, interactive complexity, etc.). Fan-out filters
 * sibling sections by patient id in linkIds.
 */
import type { Questionnaire, QuestionnaireItem } from '@medplum/fhirtypes';
import {
  GROUP_NOTE_PATIENT_SECTION_EXT,
  GROUP_NOTE_PATIENT_SECTION_LINK_ID_PREFIX,
  GROUP_NOTE_PATIENT_SECTION_PROTOTYPE_LINK_ID,
} from '@unify/fhir-model/constants';

export type GroupNotePatientParticipant = {
  patientId: string;
  displayName: string;
};

export function isGroupNotePatientSectionPrototype(item: QuestionnaireItem): boolean {
  if (item.linkId === GROUP_NOTE_PATIENT_SECTION_PROTOTYPE_LINK_ID) return true;
  return item.extension?.some(
    (e) => e.url === GROUP_NOTE_PATIENT_SECTION_EXT && e.valueBoolean === true,
  ) === true;
}

export function groupNotePatientSectionLinkId(patientId: string): string {
  return `${GROUP_NOTE_PATIENT_SECTION_LINK_ID_PREFIX}${patientId}`;
}

export function patientIdFromGroupNoteSectionLinkId(linkId: string): string | undefined {
  if (!linkId.startsWith(GROUP_NOTE_PATIENT_SECTION_LINK_ID_PREFIX)) return undefined;
  const id = linkId.slice(GROUP_NOTE_PATIENT_SECTION_LINK_ID_PREFIX.length).trim();
  return id || undefined;
}

function suffixLinkId(linkId: string | undefined, patientId: string): string {
  const base = (linkId ?? '').trim() || 'item';
  if (base.endsWith(`.${patientId}`)) return base;
  return `${base}.${patientId}`;
}

function cloneItemForPatient(item: QuestionnaireItem, patientId: string): QuestionnaireItem {
  const next: QuestionnaireItem = {
    ...item,
    linkId: suffixLinkId(item.linkId, patientId),
  };
  if (item.item?.length) {
    next.item = item.item.map((child) => cloneItemForPatient(child, patientId));
  }
  // enableWhen question refs must point at the suffixed sibling linkIds.
  if (item.enableWhen?.length) {
    next.enableWhen = item.enableWhen.map((rule) => ({
      ...rule,
      question: rule.question ? suffixLinkId(rule.question, patientId) : rule.question,
    }));
  }
  return next;
}

function clientSessionNotesLabel(displayName: string, patientId: string): string {
  const name = displayName.trim() || `Client ${patientId.slice(0, 8)}`;
  return `${name} — Session notes`;
}

function buildPatientSection(
  prototype: QuestionnaireItem,
  participant: GroupNotePatientParticipant,
): QuestionnaireItem {
  const { patientId, displayName } = participant;
  const label = clientSessionNotesLabel(displayName, patientId);
  const children = prototype.item ?? [];

  // Thin prototype (one text field): expand to a single titled text item per client.
  if (children.length === 1 && children[0]?.type === 'text') {
    const child = children[0];
    return {
      ...child,
      linkId: groupNotePatientSectionLinkId(patientId),
      text: label,
      extension: (child.extension ?? []).filter((e) => e.url !== GROUP_NOTE_PATIENT_SECTION_EXT),
      enableWhen: child.enableWhen?.map((rule) => ({
        ...rule,
        question: rule.question ? suffixLinkId(rule.question, patientId) : rule.question,
      })),
    };
  }

  return {
    ...prototype,
    linkId: groupNotePatientSectionLinkId(patientId),
    text: label,
    extension: (prototype.extension ?? []).filter((e) => e.url !== GROUP_NOTE_PATIENT_SECTION_EXT),
    item: children.map((child) => cloneItemForPatient(child, patientId)),
  };
}

/**
 * Replace the patient-section prototype with one group item per participant.
 * When `keepOnlyPatientId` is set (fan-out patient copy), keep only that section.
 */
export function expandGroupNotePatientSections(
  items: QuestionnaireItem[] | undefined,
  participants: GroupNotePatientParticipant[],
  opts?: { keepOnlyPatientId?: string },
): QuestionnaireItem[] {
  if (!items?.length) return items ?? [];

  const out: QuestionnaireItem[] = [];
  for (const item of items) {
    if (!isGroupNotePatientSectionPrototype(item)) {
      // Already-expanded patient sections from a prior save / remount.
      const existingPatientId = patientIdFromGroupNoteSectionLinkId(item.linkId ?? '');
      if (existingPatientId) {
        if (opts?.keepOnlyPatientId && existingPatientId !== opts.keepOnlyPatientId) {
          continue;
        }
        out.push(item);
        continue;
      }
      out.push(item);
      continue;
    }

    const list = opts?.keepOnlyPatientId
      ? participants.filter((p) => p.patientId === opts.keepOnlyPatientId)
      : participants;

    for (const participant of list) {
      out.push(buildPatientSection(item, participant));
    }
  }
  return out;
}

/** Clone a Questionnaire with patient sections expanded for the given participants. */
export function expandGroupNoteQuestionnaire(
  questionnaire: Questionnaire,
  participants: GroupNotePatientParticipant[],
  opts?: { keepOnlyPatientId?: string },
): Questionnaire {
  return {
    ...questionnaire,
    item: expandGroupNotePatientSections(questionnaire.item, participants, opts),
  };
}
